import { useCallback, useState } from 'react';

import type { AnchorSide } from '../anchor';

/**
 * The side a visitor explicitly moved the chat window to.
 *
 * Its own key, not `pleiground_chat_position`, whose side was written on any
 * drag and would pin visitors away from the admin's side. Written only from
 * the position-menu handlers, so a plain resize never overwrites it.
 */
const STORAGE_KEY = 'kapia_chat_anchor';

function readVisitorSide(): AnchorSide | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    const side = (parsed as { side?: unknown } | null)?.side;
    // Anything unrecognised reads as "no choice made". Never fall back to a
    // concrete side here, or the admin's default could never win.
    return side === 'left' || side === 'right' ? side : null;
  } catch {
    return null;
  }
}

export function useVisitorSide(previewMode: boolean) {
  const [visitorSide, setVisitorSide] = useState<AnchorSide | null>(() =>
    previewMode ? null : readVisitorSide(),
  );

  const commitVisitorSide = useCallback(
    (side: AnchorSide) => {
      setVisitorSide(side);
      if (previewMode) return;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ side }));
      } catch {
        // Private browsing or blocked storage: the choice still applies for
        // this page view, it just won't outlive it.
      }
    },
    [previewMode],
  );

  return { visitorSide, commitVisitorSide };
}
